import { useState, useRef, useEffect } from 'react';
import {
  MdClose, MdAdd, MdRemove, MdArrowBack, MdWifiOff, MdErrorOutline,
  MdWhatsapp, MdDeleteOutline,
} from 'react-icons/md';
import { StorefrontProducto, useCrearPedidoPublico, getFechaHoraChile, formatFechaHoraLocal } from '@almacenes/core';
import { toTitleCase, formatPrecio, clasificarError, BRAND } from '../utils';
import type { ItemCarrito } from '../types';
import { useFocusTrap } from '../hooks/use-focus-trap';
import { MarcaAD } from './MarcaAD';

const STEP_GRANEL = 0.1;
const UMBRAL_GRANEL_ADVERTENCIA = 50;
const HORARIO_MIN = '07:00';
const HORARIO_MAX = '22:00';

type PasoCarrito = 'revision' | 'contacto';

function sanitizarNombre(val: string): string {
  // Elimina caracteres de control (U+0000–U+001F) y ángulos HTML
  return val.replace(/[\x00-\x1F<>]/g, '').slice(0, 100);
}

function sanitizarTelefono(val: string): string {
  return val.replace(/[^\d\s+\-()]/g, '').slice(0, 20);
}

interface Props {
  carrito: ItemCarrito[];
  total: number;
  almacenNombre: string;
  logoUrl: string | null;
  colorFondo: string | null;
  onCambiarCantidad: (id: number, delta: number) => void;
  onEliminarItem: (id: number) => void;
  onEditarGranel: (producto: StorefrontProducto) => void;
  onCerrar: () => void;
  onPedidoCreado: (pedidoId: number) => void;
  crearPedido: ReturnType<typeof useCrearPedidoPublico>;
}

export const PanelCarrito = ({
  carrito, total, almacenNombre, logoUrl, colorFondo,
  onCambiarCantidad, onEliminarItem, onEditarGranel, onCerrar, onPedidoCreado, crearPedido,
}: Props) => {
  const [paso, setPaso] = useState<PasoCarrito>('revision');
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [horaRetiro, setHoraRetiro] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [errorMutacion, setErrorMutacion] = useState<{ mensaje: string; tipo: string } | null>(null);
  const [cooldown, setCooldown] = useState(false);
  const [undoToast, setUndoToast] = useState<{ item: ItemCarrito; timerId: ReturnType<typeof setTimeout> } | null>(null);

  const abiertaEn = useRef(Date.now());
  const cooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cerrarRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useFocusTrap(panelRef, onCerrar);

  useEffect(() => { cerrarRef.current?.focus(); }, []);
  useEffect(() => () => { if (undoToast) clearTimeout(undoToast.timerId); }, [undoToast]);
  useEffect(() => () => { if (cooldownRef.current) clearTimeout(cooldownRef.current); }, []);

  const handleEliminar = (item: ItemCarrito) => {
    if (undoToast) {
      clearTimeout(undoToast.timerId);
      onEliminarItem(undoToast.item.producto.productoAlmacenId);
    }
    const timerId = setTimeout(() => {
      onEliminarItem(item.producto.productoAlmacenId);
      setUndoToast(null);
    }, 3000);
    setUndoToast({ item, timerId });
  };

  const handleDeshacer = () => {
    if (!undoToast) return;
    clearTimeout(undoToast.timerId);
    setUndoToast(null);
  };

  const validarContacto = () => {
    const e: Record<string, string> = {};
    const nombreLimpio = nombre.trim();
    if (!nombreLimpio) e.nombre = 'Ingresa tu nombre';
    else if (nombreLimpio.length < 2) e.nombre = 'El nombre debe tener al menos 2 caracteres';

    const telefonoLimpio = telefono.trim().replace(/[\s\-()]/g, '');
    if (!telefonoLimpio) e.telefono = 'Ingresa tu teléfono';
    else if (!/^\+?\d{7,15}$/.test(telefonoLimpio)) e.telefono = 'Formato inválido — ej: +56 9 1234 5678';

    if (!horaRetiro) e.horaRetiro = 'Selecciona una hora';
    setErrores(e);
    return Object.keys(e).length === 0;
  };

  const handleConfirmar = async () => {
    if (honeypot || Date.now() - abiertaEn.current < 3000 || cooldown) return;
    if (!validarContacto()) return;

    const fechaHoraPickup = getFechaHoraChile();
    const [horas, minutos] = horaRetiro.split(':').map(Number);
    fechaHoraPickup.setHours(horas, minutos, 0, 0);
    if (fechaHoraPickup < getFechaHoraChile()) fechaHoraPickup.setDate(fechaHoraPickup.getDate() + 1);

    // ✅ Formatear SIN convertir a UTC
    const fechaHoraStr = formatFechaHoraLocal(fechaHoraPickup);

    console.log('fechaHoraPickup', fechaHoraStr, fechaHoraPickup.toISOString());
    setErrorMutacion(null);
    try {
      const result = await crearPedido.mutateAsync({
        nombreCliente: nombre.trim(),
        telefonoCliente: telefono.trim().replace(/[\s\-()]/g, ''),
        fechaHoraPickup: fechaHoraStr,
        items: carrito.map((i) => ({ productoAlmacenId: i.producto.productoAlmacenId, cantidad: i.cantidad })),
      });
      setCooldown(true);
      cooldownRef.current = setTimeout(() => setCooldown(false), 60_000);
      onPedidoCreado(result.pedidoId);
    } catch (err) {
      setErrorMutacion(clasificarError(err));
    }
  };

  const whatsappFallback = `https://wa.me/?text=${encodeURIComponent(
    `Hola, quiero hacer un pedido en ${almacenNombre}. No pude completarlo en línea.`
  )}`;

  return (
    <div className="fixed inset-0 z-30 flex" role="dialog" aria-modal="true" aria-label="Carrito de compras">
      <div
        className="flex-1 bg-black/40"
        style={{ animation: 'fadeIn 200ms ease' }}
        onClick={onCerrar}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className="w-full max-w-sm bg-white flex flex-col shadow-2xl"
        style={{ animation: 'slideInRight 280ms cubic-bezier(0.32,0.72,0,1)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          {paso === 'contacto' ? (
            <button
              onClick={() => { setPaso('revision'); setErrorMutacion(null); }}
              className="flex items-center gap-1 text-sm font-medium"
              style={{ color: BRAND.cyan }}
              aria-label="Volver a la revisión del pedido"
            >
              <MdArrowBack aria-hidden="true" />
              Volver
            </button>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              {logoUrl && (
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 overflow-hidden p-0.5"
                  style={{ backgroundColor: colorFondo || '#F3F4F9' }}
                >
                  <img src={logoUrl} alt="" aria-hidden="true" className="w-full h-full object-contain" />
                </div>
              )}
              <h2 className="text-label truncate" style={{ color: BRAND.navy }}>{almacenNombre}</h2>
              {logoUrl && <span className="text-gray-300 shrink-0">|</span>}
              <MarcaAD />
            </div>
          )}
          <button
            ref={cerrarRef}
            onClick={onCerrar}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 -mr-1"
            aria-label="Cerrar carrito"
          >
            <MdClose className="text-xl" aria-hidden="true" />
          </button>
        </div>

        {/* Barra de progreso */}
        <div className="px-4 pt-2 pb-1">
          <div className="flex gap-1" aria-hidden="true">
            <div className="h-1 flex-1 rounded-full transition-colors"
              style={{ backgroundColor: paso === 'contacto' ? BRAND.navy : BRAND.cyan }} />
            <div className="h-1 flex-1 rounded-full transition-colors"
              style={{ backgroundColor: paso === 'contacto' ? BRAND.cyan : '#E5E7EB' }} />
          </div>
          <p className="text-[10px] text-gray-400 mt-1" aria-live="polite">
            Paso {paso === 'revision' ? '1' : '2'} de 2 —{' '}
            {paso === 'revision' ? 'Revisión del pedido' : 'Tus datos'}
          </p>
        </div>

        {/* Toast deshacer */}
        {undoToast && (
          <div
            className="mx-4 mt-1 flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-xs"
            style={{ backgroundColor: BRAND.navy, color: '#fff' }}
            role="status"
          >
            <span className="truncate">"{toTitleCase(undoToast.item.producto.nombre)}" eliminado</span>
            <button onClick={handleDeshacer} className="shrink-0 font-semibold underline" style={{ color: BRAND.cyan }}>
              Deshacer
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {/* Paso 1: Revisión */}
          {paso === 'revision' && (
            <>
              {carrito.length === 0 ? (
                <p className="text-center text-muted py-8" role="status">El carrito está vacío</p>
              ) : (
                <ul className="divide-y divide-gray-100 list-none p-0" aria-label="Productos en el carrito">
                  {carrito.map((item, idx) => {
                    const esGranel = item.producto.venderPor === 'F';
                    const step = esGranel ? STEP_GRANEL : 1;
                    const pendienteEliminar = undoToast?.item.producto.productoAlmacenId === item.producto.productoAlmacenId;
                    return (
                      <li
                        key={item.producto.productoAlmacenId}
                        className={`flex items-start gap-2 py-2.5 px-2 rounded-lg transition-opacity${pendienteEliminar ? ' opacity-40' : ''}`}
                        style={{ backgroundColor: idx % 2 === 0 ? '#F0FAFE' : 'transparent' }}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium line-clamp-2 leading-snug" style={{ color: BRAND.navy }}>
                            {toTitleCase(item.producto.nombre)}
                          </p>
                          <p className="text-small mt-0.5" style={{ color: '#6B7280' }}>
                            {formatPrecio(item.producto.precioVenta)} ×{' '}
                            {esGranel ? `${item.cantidad.toLocaleString('es-CL')} kg` : `${item.cantidad} un.`}
                          </p>
                          {esGranel && item.cantidad > UMBRAL_GRANEL_ADVERTENCIA && (
                            <div
                              className="flex items-center gap-1.5 mt-1 text-[10px] font-medium rounded-lg px-2 py-1"
                              style={{ backgroundColor: '#FFF7ED', color: BRAND.orange }}
                              role="alert"
                            >
                              <span>¿Seguro? Esto equivale a {item.cantidad.toLocaleString('es-CL')} kg</span>
                              <button onClick={() => onEditarGranel(item.producto)} className="underline shrink-0 font-semibold">
                                Corregir
                              </button>
                            </div>
                          )}
                        </div>

                        {esGranel ? (
                          <button
                            onClick={() => onEditarGranel(item.producto)}
                            className="text-xs px-2 py-1 rounded-lg border transition-colors shrink-0 mt-0.5"
                            style={{ borderColor: BRAND.cyan, color: BRAND.cyan }}
                            aria-label={`Editar kg de ${item.producto.nombre}`}
                          >
                            Editar kg
                          </button>
                        ) : (
                          <div
                            className="flex items-center gap-1 shrink-0 mt-0.5"
                            role="group"
                            aria-label={`Cantidad de ${item.producto.nombre}`}
                          >
                            <button
                              onClick={() => onCambiarCantidad(item.producto.productoAlmacenId, -step)}
                              className="w-6 h-6 flex items-center justify-center rounded-full hover:opacity-80 transition-opacity"
                              style={{ backgroundColor: BRAND.cyan, color: '#fff' }}
                              aria-label={`Quitar ${step} de ${item.producto.nombre}`}
                            >
                              <MdRemove className="text-sm" aria-hidden="true" />
                            </button>
                            <span className="text-xs font-semibold w-5 text-center" style={{ color: BRAND.navy }}>
                              {item.cantidad}
                            </span>
                            <button
                              onClick={() => onCambiarCantidad(item.producto.productoAlmacenId, step)}
                              disabled={item.producto.venderPor === 'U' && item.producto.stock != null && item.cantidad >= item.producto.stock}
                              className="w-6 h-6 flex items-center justify-center rounded-full hover:opacity-80 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
                              style={{ backgroundColor: BRAND.cyan, color: '#fff' }}
                              aria-label={`Agregar ${step} más de ${item.producto.nombre}`}
                            >
                              <MdAdd className="text-sm" aria-hidden="true" />
                            </button>
                          </div>
                        )}

                        <p className="text-xs font-bold w-14 text-right shrink-0 mt-0.5" style={{ color: BRAND.navy }}>
                          {formatPrecio(item.subtotal)}
                        </p>
                        <button
                          onClick={() => handleEliminar(item)}
                          disabled={pendienteEliminar}
                          className="shrink-0 mt-0.5 p-0.5 text-gray-300 hover:text-red-400 transition-colors disabled:opacity-30"
                          aria-label={`Eliminar ${item.producto.nombre} del carrito`}
                        >
                          <MdDeleteOutline className="text-base" aria-hidden="true" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {carrito.length > 0 && (
                <div className="border-t border-gray-100 pt-2 flex justify-between">
                  <span className="text-sm font-semibold text-gray-700">Total estimado</span>
                  <span className="text-sm font-bold" style={{ color: BRAND.navy }}>{formatPrecio(total)}</span>
                </div>
              )}
            </>
          )}

          {/* Paso 2: Contacto */}
          {paso === 'contacto' && (
            <div className="space-y-3">
              <FormField
                id="carrito-nombre" label="Nombre" type="text"
                placeholder="Tu nombre completo" value={nombre}
                onChange={(v) => setNombre(sanitizarNombre(v))}
                maxLength={100} error={errores.nombre} errorId="err-nombre"
              />
              <FormField
                id="carrito-telefono" label="Teléfono" type="tel"
                placeholder="+56 9 XXXX XXXX" value={telefono}
                onChange={(v) => setTelefono(sanitizarTelefono(v))}
                maxLength={20} error={errores.telefono} errorId="err-telefono"
                hint="Ej: +56 9 1234 5678"
              />
              <FormField
                id="carrito-hora" label="Hora de retiro" type="time"
                value={horaRetiro} onChange={setHoraRetiro}
                min={HORARIO_MIN} max={HORARIO_MAX}
                error={errores.horaRetiro} errorId="err-hora"
                hint="El almacén confirmará si el horario está disponible"
              />

              {errorMutacion && (
                <div
                  className="rounded-xl p-3 flex flex-col gap-2 text-sm"
                  style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }}
                  role="alert"
                >
                  <div className="flex items-start gap-2">
                    {errorMutacion.tipo === 'red'
                      ? <MdWifiOff className="text-lg shrink-0 mt-0.5" aria-hidden="true" />
                      : <MdErrorOutline className="text-lg shrink-0 mt-0.5" aria-hidden="true" />}
                    <p>{errorMutacion.mensaje}</p>
                  </div>
                  <a
                    href={whatsappFallback}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs font-medium underline"
                    style={{ color: '#16A34A' }}
                  >
                    <MdWhatsapp className="text-base" aria-hidden="true" />
                    Pedir por WhatsApp
                  </a>
                </div>
              )}

              <p className="text-[10px] text-gray-400 text-center">
                Pedido en {almacenNombre}. El almacén confirmará disponibilidad y coordinará contigo.
              </p>

              {/* Honeypot — invisible para humanos */}
              <input
                type="text" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 space-y-2">
          {paso === 'contacto' && (
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-500">Total estimado</span>
              <span className="font-bold" style={{ color: BRAND.navy }}>{formatPrecio(total)}</span>
            </div>
          )}
          {paso === 'revision' ? (
            <button
              onClick={() => setPaso('contacto')}
              disabled={carrito.length === 0}
              className="btn-primary w-full py-3 rounded-xl text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Continuar al formulario de contacto"
            >
              Continuar → Tus datos
            </button>
          ) : (
            <button
              onClick={handleConfirmar}
              disabled={crearPedido.isPending || cooldown}
              className="w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-opacity hover:opacity-90"
              style={{ backgroundColor: BRAND.navy }}
              aria-busy={crearPedido.isPending}
            >
              {crearPedido.isPending ? 'Enviando…' : cooldown ? 'Pedido enviado' : 'Confirmar pedido'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Campo de formulario reutilizable ─────────────────────────────────────────

interface FormFieldProps {
  id: string;
  label: string;
  type: string;
  placeholder?: string;
  value: string;
  onChange: (val: string) => void;
  maxLength?: number;
  min?: string;
  max?: string;
  error?: string;
  errorId?: string;
  hint?: string;
}

const FormField = ({ id, label, type, placeholder, value, onChange, maxLength, min, max, error, errorId, hint }: FormFieldProps) => (
  <div>
    <label htmlFor={id} className="text-small text-gray-600 block mb-1">{label}</label>
    <input
      id={id} type={type} placeholder={placeholder} value={value}
      onChange={(e) => onChange(e.target.value)}
      maxLength={maxLength} min={min} max={max}
      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-[#0095C2] focus:bg-[#F0FAFE] transition-colors"
      style={{ '--tw-ring-color': BRAND.cyan } as React.CSSProperties}
      aria-invalid={!!error}
      aria-describedby={error ? errorId : hint ? `${id}-hint` : undefined}
    />
    {error
      ? <p id={errorId} className="text-small mt-1" style={{ color: '#EF4444' }} role="alert">{error}</p>
      : hint && <p id={`${id}-hint`} className="text-[10px] text-gray-400 mt-1">{hint}</p>}
  </div>
);
